import { HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { ApiResponsePaged } from '@core/interfaces/api.interface';
import { BaseService } from '@core/services/base.service';
import { mapPagedResponse } from '@core/utils/mapper-response-paged.util';
import { Observable } from 'rxjs';
import type { AlertRow } from '../models/alert-row.model';
import type { RelatedAlertRow } from '../models/related-alert-row.model';



/**
 * @description
 * Input para obtener las alertas relacionadas.
 */
export interface RelatedAlertsQueryParams {
  alertId: string;
  businessesId: string;
  pageNumber?: number;
  pageSize?: number;
}



/**
 * @description
 * Input para obtener las alertas de última fecha.
 */
export interface AlertsQueryParams {
  BusinessesId?: string;
  businessesId?: string;
  pageNumber: number;
  pageSize: number;
  RangeDateFrom?: string;
  RangeDateTo?: string;
  RangeDateModifiedFrom?: string;
  RangeDateModifiedTo?: string;
  orderStatusName?: string;
  dateFilter?: string | string[];
  fleetName?: string;
  fleetId?: string;
  fleetIds?: string[];
  licensePlate?: string;
  vehicleId?: string;
  vehicleTypeName?: string;
  driverFullName?: string;
  isActive?: boolean;
  isDeleted?: boolean;
  isClosed?: boolean;
  /** Parámetro legacy del API (`isclosed`). */
  isclosed?: boolean;
  whereAnd?: string;
  orderBy?: string | string[];
  alertCheckListStateId?: string;
  priority?: boolean;
  question?: string;
  comments?: string;
  milleage?: string;
  hours?: string;
  relatedItems?: number;
  orderNumber?: number;
  maintenanceWorkName?: string;
  supervisorFullName?: string;
  checkListName?: string;
}



/**
 * @description
 * Input para cerrar una alerta.
 */
export interface CloseAlertRequest {
  alertId: string;
  businessesId?: string;
  observations?: string;
  alertCheckListStateId: string;
}



/**
 * @description
 * Input para asociar una alerta a un trabajo de mantenimiento.
 */
export interface AssociateWorkAlertRequest {
  alertId: string;
  maintenanceWorkId: string;
  businessesId?: string;
  alertCheckListStateId: string;
}


/**
 * @description
 * Input para asociar una alerta a una orden de trabajo.
 */
export interface AssociateWorkOrderAlertRequest {
  alertId: string;
  workOrderId: string;
  businessesId?: string;
  observations?: string;
  alertCheckListStateId: string;
}



/**
 * @description
 * Input para actualizar las observaciones de una alerta.
 */
export interface UpdateAlertObservationsRequest {
  alertId: string;
  businessesId?: string;
  observations?: string;
}

export interface FollowUpAlertRequest {
  alertId: string;
  businessesId?: string;
  observations?: string;
  alertCheckListStateId: string;
}



@Injectable({ providedIn: 'root' })


/**
 * @description
 * Servicio para obtener las alertas de última fecha.
 */
export class VehicleAlertsService extends BaseService {
  constructor() {
    super('vehicles');
  }


  /**
   * @description
   * Obtiene las alertas de última fecha paginadas.
   */
  getAllLastDatePaged(query: AlertsQueryParams): Observable<ApiResponsePaged<AlertRow>> {
    return this.GET<HttpResponse<AlertRow[]>>('alerts/lastDate', query, {
      observe: 'response',
    }).pipe(mapPagedResponse<AlertRow>());
  }



  /**
   * @description
   * Exporta las alertas de última fecha a Excel.
   */
  exportAlerts(query: AlertsQueryParams): Observable<Blob> {
    return this.GET('alerts/lastDate/excel', query, { responseType: 'blob' });
  }


  /**
   * @description
   * Obtiene las alertas relacionadas paginadas.
   */
  getRelatedAlertsPaged(
    query: RelatedAlertsQueryParams,
  ): Observable<ApiResponsePaged<RelatedAlertRow>> {
    return this.GET<HttpResponse<RelatedAlertRow[]>>('alerts/relatedAlerts', query, {
      observe: 'response',
    }).pipe(mapPagedResponse<RelatedAlertRow>());
  }


  /**
   * @description
   * Cierra una alerta.
   */
  closeAlert(request: CloseAlertRequest): Observable<void> {
    return this.POST<void>('alerts/actions/close', request);
  }


  /**
   * @description
   * Asocia una alerta a un trabajo de mantenimiento.
   */
  associateWork(request: AssociateWorkAlertRequest): Observable<void> {
    return this.POST<void>('alerts/actions/associate-work', request);
  }


  /**
   * @description
   * Asocia una alerta a una orden de trabajo.
   */
  associateWorkOrder(request: AssociateWorkOrderAlertRequest): Observable<void> {
    return this.POST<void>('alerts/actions/associate-work-order', request);
  }


  /**
   * @description
   * Actualiza las observaciones de una alerta.
   */
  updateObservations(request: UpdateAlertObservationsRequest): Observable<void> {
    return this.POST<void>('alerts/actions/update-observations', request);
  }

  followUp(request: FollowUpAlertRequest): Observable<void> {
    return this.POST<void>('alerts/actions/follow-up', request);
  }

  getCountHome(businessesId: string): Observable<AlertsCountHome> {
    return this.GET<AlertsCountHome>('alerts/lastDate/countHome', { businessesId });
  }
}

export interface AlertsCountHome {
  totalAbiertas: number;
  totalAbiertasPrioritarias: number;
  totalEnProceso: number;
  totalEnProcesoPrioritarias: number;
}


